/**
 * Fixed-position placement for a floating element (a menu, a tooltip bubble)
 * rendered through a portal, so no `overflow` ancestor (the DataTable's
 * horizontal scroller, a modal body) can clip it. The element sits under its
 * anchor (or over it with side 'top'), flips to the other side when it would
 * run past the viewport edge, and is clamped inside the viewport horizontally.
 * It follows the anchor on any scroll and on resize.
 *
 * Pass the elements themselves (from callback refs), so the effect re-runs
 * when a portal mounts its content a tick after `open` turns true. Until the
 * first measurement the element renders hidden, so it never flashes at 0,0.
 */
import { useEffect, useLayoutEffect, useState, type CSSProperties } from 'react'

// Measure before paint in the browser; plain effect on the server (no SSR warning).
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect

export interface AnchoredOptions {
  side?: 'bottom' | 'top'
  align?: 'start' | 'center' | 'end'
  /** Space between the anchor and the element, px. */
  gap?: number
  /** Minimum distance kept from the viewport edges, px. */
  edge?: number
}

export interface AnchorRect {
  top: number
  bottom: number
  left: number
  right: number
  width: number
}

/** Pure placement math (exported for tests): where the element's top-left corner goes. */
export function placeFloating(
  anchor: AnchorRect,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  { side = 'bottom', align = 'start', gap = 0, edge = 8 }: AnchoredOptions = {},
): { top: number; left: number } {
  const below = anchor.bottom + gap
  const above = anchor.top - gap - size.height
  const fitsBelow = below + size.height <= viewport.height - edge
  const fitsAbove = above >= edge
  let top = side === 'bottom' ? below : above
  if (side === 'bottom' && !fitsBelow && fitsAbove) top = above
  if (side === 'top' && !fitsAbove && fitsBelow) top = below
  const raw =
    align === 'start' ? anchor.left : align === 'end' ? anchor.right - size.width : anchor.left + anchor.width / 2 - size.width / 2
  const left = Math.min(Math.max(raw, edge), Math.max(edge, viewport.width - edge - size.width))
  return { top: Math.round(top), left: Math.round(left) }
}

export function useAnchoredPosition(
  anchor: HTMLElement | null,
  floating: HTMLElement | null,
  open: boolean,
  { side = 'bottom', align = 'start', gap = 0, edge = 8 }: AnchoredOptions = {},
): CSSProperties | undefined {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  useIsoLayoutEffect(() => {
    if (!open || !anchor || !floating) {
      setPos(null)
      return
    }
    const place = () => {
      const next = placeFloating(
        anchor.getBoundingClientRect(),
        { width: floating.offsetWidth, height: floating.offsetHeight },
        { width: document.documentElement.clientWidth, height: window.innerHeight },
        { side, align, gap, edge },
      )
      setPos((p) => (p && p.top === next.top && p.left === next.left ? p : next))
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, anchor, floating, side, align, gap, edge])

  if (!open) return undefined
  return { position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? undefined : 'hidden' }
}
